import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";

/**
 * AuthModal — reusable login/signup modal.
 *
 * Props:
 *   initialMode   "login" | "signup"   — which tab to open on
 *   prefillEmail  string               — pre-fill the email field (invite flow)
 *   onClose       () => void           — called when user dismisses
 *   onSuccess     () => void           — called after successful auth
 */
export default function AuthModal({ initialMode = "login", prefillEmail = "", onClose, onSuccess }) {
  const { login, signup } = useAuth();

  const [mode, setMode]             = useState(initialMode);
  const [email, setEmail]           = useState(prefillEmail);
  const [password, setPass]         = useState("");
  const [error, setError]           = useState("");
  const [loading, setLoading]       = useState(false);
  const [tosAccepted, setTos]       = useState(false);
  const [privacyRead, setPrivacy]   = useState(false);

  // Reset consent checkboxes when switching between login and signup
  useEffect(() => {
    setTos(false);
    setPrivacy(false);
    setError("");
  }, [mode]);

  // If the caller changes prefillEmail after mount (shouldn't happen but defensive)
  useEffect(() => {
    if (prefillEmail) setEmail(prefillEmail);
  }, [prefillEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        // Capture timestamp at the moment of submission — used as the consent record.
        await signup(email, password, new Date().toISOString());
      }
      onSuccess?.();
    } catch (err) {
      // Firebase error messages are usable but verbose — trim the prefix
      setError(err.message.replace("Firebase: ", "").replace(/ \(auth\/[^)]+\)\.?/, ""));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="auth-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      role="dialog"
      aria-modal="true"
      aria-label={mode === "login" ? "Log in" : "Sign up"}
    >
      <div className="auth-card">

        <button
          type="button"
          className="auth-close-btn"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <div className="auth-card-brand">
          <span className="auth-card-brand-icon">👶</span>
        </div>

        <h2 className="auth-card-heading">
          {mode === "login" ? "Welcome back" : "Create your account"}
        </h2>
        <p className="auth-card-sub">
          {mode === "login"
            ? "Log in to Baby Tracker"
            : "Start tracking in seconds — it's free"}
        </p>

        {/* Pill-style tab switcher */}
        <div className="auth-tabs">
          <button
            type="button"
            className={`auth-tab${mode === "login" ? " active" : ""}`}
            onClick={() => { setMode("login"); setError(""); }}
          >
            Log in
          </button>
          <button
            type="button"
            className={`auth-tab${mode === "signup" ? " active" : ""}`}
            onClick={() => { setMode("signup"); setError(""); }}
          >
            Sign up
          </button>
        </div>

        {error && (
          <div className="auth-error" role="alert">{error}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="auth-field">
            <label htmlFor="auth-email">Email address</label>
            <input
              id="auth-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              autoFocus={!prefillEmail}
              placeholder="you@example.com"
            />
          </div>

          <div className="auth-field">
            <label htmlFor="auth-password">Password</label>
            <input
              id="auth-password"
              type="password"
              value={password}
              onChange={(e) => setPass(e.target.value)}
              required
              minLength={6}
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              autoFocus={!!prefillEmail}
              placeholder={mode === "signup" ? "Minimum 6 characters" : "••••••••"}
            />
            {mode === "signup" && (
              <span className="auth-field-hint">Minimum 6 characters</span>
            )}
          </div>

          {mode === "signup" && (
            <div className="auth-consent">
              <label className="auth-consent-item">
                <input
                  type="checkbox"
                  checked={tosAccepted}
                  onChange={(e) => setTos(e.target.checked)}
                  required
                />
                <span>
                  I agree to the{" "}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">
                    Terms of Service
                  </a>
                </span>
              </label>

              <label className="auth-consent-item">
                <input
                  type="checkbox"
                  checked={privacyRead}
                  onChange={(e) => setPrivacy(e.target.checked)}
                  required
                />
                <span>
                  I have read the{" "}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                  , including that health data will be stored
                </span>
              </label>
            </div>
          )}

          <button
            type="submit"
            className="auth-submit-btn"
            disabled={loading || (mode === "signup" && (!tosAccepted || !privacyRead))}
          >
            {loading ? "Please wait…" : mode === "login" ? "Log in" : "Create account"}
          </button>
        </form>

      </div>
    </div>
  );
}
